/**
 * 文本属性读取（`<text>` / `<tspan>`）。
 * 读取顺序：自身属性 → 祖先 `<text>` 继承 → 默认值（SVG 的 font-* / fill 都会向下继承）。
 */
import { SVG_NS } from '../dom';
import { readPresentation } from './props';

export type TextAnchor = 'start' | 'middle' | 'end';
export type FontWeight = 'normal' | 'bold';
export type FontStyle = 'normal' | 'italic';

/** 中文字体栈：CJK 优先，回落到系统无衬线 */
export const DEFAULT_FONT_STACK =
  "'PingFang SC', 'Microsoft YaHei', 'Source Han Sans SC', 'Noto Sans CJK SC', system-ui, sans-serif";

/**
 * 字体清单：**具体字体名**，不是「中文无衬线 / 等宽」这类类别名 ——
 * 类别名写进文件后，作者看不出自己实际用的是哪款字体，换台机器也无从回落。
 * 每项给的是**可回落的栈**（本机缺第一款就按顺序往下走），写进 font-family 的就是整段栈。
 */
export interface FontChoice {
  /** 显示名（清单里用它自己的字体渲染） */
  label: string;
  /** 写进 font-family 的值 */
  value: string;
}

export const FONT_LIST: ReadonlyArray<FontChoice> = [
  { label: '微软雅黑', value: "'Microsoft YaHei', 'PingFang SC', sans-serif" },
  { label: '苹方', value: "'PingFang SC', 'Microsoft YaHei', sans-serif" },
  { label: '思源黑体', value: "'Source Han Sans SC', 'Noto Sans CJK SC', 'Microsoft YaHei', sans-serif" },
  { label: '黑体', value: "SimHei, 'Heiti SC', sans-serif" },
  { label: '等线', value: "DengXian, 'Microsoft YaHei', sans-serif" },
  { label: '宋体', value: "SimSun, 'Songti SC', serif" },
  { label: '思源宋体', value: "'Source Han Serif SC', 'Noto Serif CJK SC', SimSun, serif" },
  { label: '仿宋', value: "FangSong, 'FangSong_GB2312', serif" },
  { label: '楷体', value: "KaiTi, 'Kaiti SC', serif" },
  { label: '华文行楷', value: "STXingkai, 'Xingkai SC', cursive" },
  { label: 'Arial', value: "Arial, 'Helvetica Neue', Helvetica, sans-serif" },
  { label: 'Helvetica', value: "'Helvetica Neue', Helvetica, Arial, sans-serif" },
  { label: 'Inter', value: "Inter, 'Segoe UI', Arial, sans-serif" },
  { label: 'Times New Roman', value: "'Times New Roman', Times, serif" },
  { label: 'Georgia', value: 'Georgia, Times, serif' },
  { label: 'Consolas', value: 'Consolas, Menlo, monospace' },
  { label: 'JetBrains Mono', value: "'JetBrains Mono', Consolas, monospace" },
  { label: 'Courier New', value: "'Courier New', Courier, monospace" },
];

export interface TextProps {
  /** 全文（多行以 \n 连接） */
  content: string;
  lines: string[];
  fontFamily: string;
  fontSize: number;
  fill: string;
  fontWeight: FontWeight;
  fontStyle: FontStyle;
  textAnchor: TextAnchor;
  letterSpacing: number;
  /** 逐字 tspan 的竖排 */
  vertical: boolean;
  hidden: boolean;
}

const TEXT_TAGS = new Set(['text', 'tspan', 'textpath']);

export const isTextLike = (el: Element): boolean => TEXT_TAGS.has(el.tagName.toLowerCase());

/** 找到所属的 `<text>`（tspan 归属其父级） */
export function textOwner(el: Element): Element | null {
  let cur: Element | null = el;
  while (cur) {
    const tag = cur.tagName.toLowerCase();
    if (tag === 'text') return cur;
    if (tag === 'svg') return null;
    cur = cur.parentElement;
  }
  return null;
}

const ownText = (el: Element): string =>
  Array.from(el.childNodes)
    .filter((n) => n.nodeType === 3)
    .map((n) => n.nodeValue ?? '')
    .join('');

/** 各行文本：有 `<tspan>` 就逐条取，否则取自身文本节点 */
export function textLines(el: Element): string[] {
  const tspans = Array.from(el.children).filter((c) => c.tagName.toLowerCase() === 'tspan');
  if (tspans.length > 0) return tspans.map((t) => t.textContent ?? '');
  const own = ownText(el);
  return [own];
}

export const textContentOf = (el: Element): string => textLines(el).join('\n');

/** 竖排判定：≥2 个 tspan、x 相同、y 递增（逐字竖排留下的印记） */
export function isVerticalText(el: Element): boolean {
  const tspans = Array.from(el.children).filter((c) => c.tagName.toLowerCase() === 'tspan');
  if (tspans.length < 2) return false;
  const xs = tspans.map((t) => t.getAttribute('x'));
  const ys = tspans.map((t) => Number(t.getAttribute('y') ?? Number.NaN));
  if (new Set(xs).size !== 1 || xs[0] === null) return false;
  for (let i = 1; i < ys.length; i += 1) if (!(ys[i]! > ys[i - 1]!)) return false;
  return true;
}

/** 沿祖先链取值（含内联 style 优先），用于 font-* / fill 的继承 */
function inherit(el: Element, name: string): string | null {
  let cur: Element | null = el;
  while (cur && cur.tagName.toLowerCase() !== 'svg') {
    const value = readPresentation(cur, name);
    if (value !== null) return value;
    cur = cur.parentElement;
  }
  return null;
}

const num = (raw: string | null, fallback: number): number => {
  const v = Number.parseFloat(raw ?? '');
  return Number.isFinite(v) ? v : fallback;
};

export function readTextProps(el: Element): TextProps {
  const lines = textLines(el);
  const weight = inherit(el, 'font-weight');
  const style = inherit(el, 'font-style');
  const anchor = inherit(el, 'text-anchor');
  return {
    content: lines.join('\n'),
    lines,
    fontFamily: inherit(el, 'font-family') ?? DEFAULT_FONT_STACK,
    fontSize: num(inherit(el, 'font-size'), 16),
    fill: inherit(el, 'fill') ?? '#000000',
    fontWeight: weight === 'bold' || Number.parseInt(weight ?? '', 10) >= 600 ? 'bold' : 'normal',
    fontStyle: style === 'italic' || style === 'oblique' ? 'italic' : 'normal',
    textAnchor: anchor === 'middle' || anchor === 'end' ? anchor : 'start',
    letterSpacing: num(inherit(el, 'letter-spacing'), 0),
    vertical: isVerticalText(el),
    hidden: el.getAttribute('display') === 'none',
  };
}

/** 行高（写入 tspan 的 y 步进用） */
export const LINE_HEIGHT_RATIO = 1.2;
/** 竖排逐字的步进比例 */
export const VERTICAL_RATIO = 1.1;

export const newTspan = (doc: Document): Element => doc.createElementNS(SVG_NS, 'tspan');
